<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { updateBusiness, updatePreferences } from '@/api/account';
import { useAuthStore } from '@/stores/auth';
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar';
import { CURRENCIES } from '@/data/offeringHelpers';
import {
    createFormatters,
    DEFAULT_PREFERENCES,
    UNITS_BY_SYSTEM,
    type ResolvedPreferences,
} from '@/composables/usePreferences';

definePage({ meta: { requiresAuth: true } });

const auth = useAuthStore();

const form = ref<ResolvedPreferences>({ ...DEFAULT_PREFERENCES });
// La moneda no vive en `preferences` sino en el Provider (la heredan las
// offerings), pero se edita acá porque es donde el usuario la busca.
const currency = ref('EUR');

const saving = ref(false);

const canEdit = computed(() => auth.providerUser?.role === 'owner' || auth.providerUser?.role === 'admin');

const DATE_FORMATS = [
    { title: 'DD/MM/YYYY (31/12/2026)', value: 'DD/MM/YYYY' },
    { title: 'MM/DD/YYYY (12/31/2026)', value: 'MM/DD/YYYY' },
    { title: 'YYYY-MM-DD (2026-12-31)', value: 'YYYY-MM-DD' },
];
const TIME_FORMATS = [
    { title: '24-hour (15:30)', value: '24h' },
    { title: '12-hour (3:30 PM)', value: '12h' },
];
const NUMBER_FORMATS = [
    { title: '1,234.56', value: 'comma-dot' },
    { title: '1.234,56', value: 'dot-comma' },
    { title: '1 234,56', value: 'space-comma' },
];
const WEEK_STARTS = [
    { title: 'Monday', value: 'monday' },
    { title: 'Sunday', value: 'sunday' },
];
const CURRENCY_DISPLAYS = [
    { title: 'Code (EUR 100)', value: 'code' },
    { title: 'Symbol (€100)', value: 'symbol' },
];
const MEASUREMENT_SYSTEMS = [
    { title: 'Metric (m, °C, kg, bar)', value: 'metric' },
    { title: 'Imperial (ft, °F, lb, psi)', value: 'imperial' },
];
const DISTANCE_UNITS = [
    { title: 'Meters', value: 'meters' },
    { title: 'Feet', value: 'feet' },
];
const TEMPERATURE_UNITS = [
    { title: 'Celsius', value: 'celsius' },
    { title: 'Fahrenheit', value: 'fahrenheit' },
];
const WEIGHT_UNITS = [
    { title: 'Kilograms', value: 'kilograms' },
    { title: 'Pounds', value: 'pounds' },
];
const AIR_PRESSURE_UNITS = [
    { title: 'Bars', value: 'bars' },
    { title: 'PSI', value: 'psi' },
];

onMounted(async () => {
    await auth.refreshMe();
    form.value = { ...DEFAULT_PREFERENCES, ...(auth.provider?.preferences ?? {}) };
    currency.value = auth.provider?.currency ?? 'EUR';
});

// El sistema de medida es un atajo: setea las cuatro unidades de una. Después
// cada una queda editable por separado, porque hay centros que mezclan (típico:
// profundidad en metros y presión en PSI). Mismo comportamiento que la app móvil.
watch(() => form.value.measurementSystem, (system, previous) => {
    if (!previous || system === previous) return;
    Object.assign(form.value, UNITS_BY_SYSTEM[system]);
});

/**
 * Ejemplos en vivo para que se vea qué hace cada opción antes de guardar. Se
 * formatea con los valores del formulario, no con los del store: si usara el
 * store no cambiaría nada hasta guardar y el preview no serviría para elegir.
 */
const preview = computed(() => {
    const f = createFormatters(form.value, currency.value);
    const now = new Date();
    return {
        date: f.formatDate(now),
        time: f.formatTime(now),
        number: f.formatNumber(1234.56),
        price: f.formatCurrency(1234.5),
    };
});

async function save() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    saving.value = true;
    try {
        // Dos endpoints porque la moneda es del Provider y el resto del
        // sub-documento `preferences`. Se manda la moneda solo si cambió.
        if (currency.value !== (auth.provider?.currency ?? 'EUR')) {
            await updateBusiness({ currency: currency.value });
        }
        await updatePreferences({ ...form.value });
        await auth.refreshMe();
        notifySuccess('Preferences saved.');
    } catch (e) {
        notifyError(apiMessage(e, 'Could not save.'));
    } finally {
        saving.value = false;
    }
}
</script>

<template>
  <div>
    <v-alert
      v-if="!canEdit"
      type="info"
      variant="tonal"
      density="comfortable"
      class="mb-6"
    >
      Only the owner and admins can change these — they affect what the whole team sees.
    </v-alert>


    <v-form @submit.prevent="save">
      <!-- Regional -->
      <v-card border flat class="pa-6 mb-6">
        <h2 class="text-h6 font-weight-bold mb-1">Regional formats</h2>
        <p class="text-body-2 text-medium-emphasis mb-5">
          Applied wherever the portal shows a date, a number or a price.
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.dateFormat"
              :items="DATE_FORMATS"
              label="Date format"
              prepend-inner-icon="ri-calendar-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.timeFormat"
              :items="TIME_FORMATS"
              label="Time format"
              prepend-inner-icon="ri-time-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.numberFormat"
              :items="NUMBER_FORMATS"
              label="Number format"
              prepend-inner-icon="ri-hashtag"
              :disabled="saving || !canEdit"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.weekStartsOn"
              :items="WEEK_STARTS"
              label="Week starts on"
              prepend-inner-icon="ri-calendar-2-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- Moneda -->
      <v-card border flat class="pa-6 mb-6">
        <h2 class="text-h6 font-weight-bold mb-1">Currency</h2>
        <p class="text-body-2 text-medium-emphasis mb-5">
          Every offering and season price uses the account currency — it isn't picked per offer.
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="currency"
              :items="CURRENCIES"
              label="Account currency"
              prepend-inner-icon="ri-money-dollar-circle-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.currencyDisplay"
              :items="CURRENCY_DISPLAYS"
              label="Show prices as"
              prepend-inner-icon="ri-price-tag-3-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- Unidades -->
      <v-card border flat class="pa-6 mb-6">
        <h2 class="text-h6 font-weight-bold mb-1">Units</h2>
        <p class="text-body-2 text-medium-emphasis mb-5">
          Picking a system sets all four at once. Adjust any of them if your centre mixes units.
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.measurementSystem"
              :items="MEASUREMENT_SYSTEMS"
              label="Unit system"
              prepend-inner-icon="ri-ruler-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
        </v-row>
        <v-divider class="my-4" />
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.distanceUnit"
              :items="DISTANCE_UNITS"
              label="Depth & distance"
              prepend-inner-icon="ri-arrow-up-down-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.temperatureUnit"
              :items="TEMPERATURE_UNITS"
              label="Temperature"
              prepend-inner-icon="ri-temp-cold-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.weightUnit"
              :items="WEIGHT_UNITS"
              label="Weight"
              prepend-inner-icon="ri-scales-3-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.airPressureUnit"
              :items="AIR_PRESSURE_UNITS"
              label="Air pressure"
              prepend-inner-icon="ri-gas-station-line"
              :disabled="saving || !canEdit"
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- Vista previa: el selector deja de ser abstracto. -->
      <v-card border flat class="pa-6 mb-6">
        <h2 class="text-h6 font-weight-bold mb-4">Preview</h2>
        <v-row>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Date</div>
            <div class="text-body-1 font-weight-medium">{{ preview.date }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Time</div>
            <div class="text-body-1 font-weight-medium">{{ preview.time }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Number</div>
            <div class="text-body-1 font-weight-medium">{{ preview.number }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Price</div>
            <div class="text-body-1 font-weight-medium">{{ preview.price }}</div>
          </v-col>
        </v-row>
        <p class="text-caption text-medium-emphasis mt-4 mb-0">
          The preview updates as you pick, but nothing is stored until you save.
        </p>
      </v-card>

      <div class="d-flex justify-end">
        <v-btn type="submit" color="primary" :loading="saving" :disabled="!canEdit">
          Save changes
        </v-btn>
      </div>
    </v-form>
  </div>
</template>
